import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Anchor, Grid } from '@mantine/core';
import { Link } from 'react-router-dom';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import AppCard from '../../common/AppCard';
import CreationWizardButton from './CreationWizardButton';
import MediaDropzone from '../media/MediaDropzone';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import { uploadFileToStorage } from '../../../helpers/awsHelper';

const StoreCreationWebstoreStep = ({
  loading,
  createOptions,
  formState,
  setFormState
}) => {
  const { state: authState } = useContext(AuthContext);
  const [isImageUploading, setIsImageUploading] = useState(false);

  const showStoreOrStockThemeOptions =
    formState.copyContent.ecomStore || formState.ecomStockContent.length > 0;
  const chooseNewThemeSelected =
    !formState.copyThemeFromStockContent && !formState.copyContent.copyWebstore;

  const stockContentThemes =
    createOptions?.ecomStockContent
      .filter((s) =>
        formState.ecomStockContent.some(
          (c) => c.pkEcomStockContent === s.pkEcomStockContent
        )
      )
      .sort((a, b) => a.title.localeCompare(b.title)) || [];

  return (
    <AppStack style={{ gap: 16, paddingTop: 10 }}>
      <AppStack style={{ gap: 5 }}>
        <AppFlexbox
          style={{
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
            Banner image
          </AppText>
        </AppFlexbox>
        <AppStack style={{ gap: 8 }}>
          <AppStack>
            <MediaDropzone
              disabled={formState.loading}
              disableDirectUrl
              files={[]}
              height={175}
              loading={isImageUploading}
              maxSelectCount={1}
              onUpload={(media) => {
                if (media[0]) {
                  setIsImageUploading(true);
                  uploadFileToStorage(
                    `${authState.userData.user.pkUser}-ecom-store-banner`,
                    media[0].file,
                    (location) => {
                      setFormState((c) => ({
                        ...c,
                        storeSettings: {
                          ...c.storeSettings,
                          bannerImageUrl: location
                        }
                      }));
                      setIsImageUploading(false);
                    },
                    () => {
                      setIsImageUploading(false);
                      triggerNotification();
                    }
                  );
                }
                else {
                  setFormState((c) => ({
                    ...c,
                    storeSettings: {
                      ...c.storeSettings,
                      bannerImageUrl: null
                    }
                  }));
                }
              }}
              selectedPreview={formState.storeSettings?.bannerImageUrl}
            />
          </AppStack>
        </AppStack>
      </AppStack>

      {showStoreOrStockThemeOptions && (
        <AppStack style={{ gap: 5 }}>
          <AppStack style={{ gap: 0 }}>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              Copy theme
            </AppText>
            <AppText style={{ fontSize: 14, color: '#666' }}>
              Choose where to copy the theme from.
            </AppText>
          </AppStack>
          <AppCard radius="md" shadow="none" style={{ padding: 8 }} withBorder>
            <Grid>
              {formState.copyContent.ecomStore && (
                <Grid.Col span={6}>
                  <CreationWizardButton
                    description="Use the same theme as the store you are copying"
                    label="Copy store theme"
                    onClick={() => {
                      setFormState({
                        ...formState,
                        copyThemeFromStockContent: false,
                        copyContent: {
                          ...formState.copyContent,
                          copyWebstore: true
                        }
                      });
                    }}
                    selected={formState.copyContent.copyWebstore}
                  />
                </Grid.Col>
              )}
              {formState.ecomStockContent.length > 0 && (
                <Grid.Col span={6}>
                  <CreationWizardButton
                    description="Custom theme created for stock content"
                    label="Stock content theme"
                    onClick={() => {
                      setFormState({
                        ...formState,
                        copyThemeFromStockContent: true,
                        copyContent: {
                          ...formState.copyContent,
                          copyWebstore: false
                        }
                      });
                    }}
                    selected={formState.copyThemeFromStockContent}
                  />
                </Grid.Col>
              )}
              <Grid.Col span={6}>
                <CreationWizardButton
                  description="Start from scratch with a new theme"
                  label="Choose new theme"
                  onClick={() => {
                    setFormState({
                      ...formState,
                      copyThemeFromStockContent: false,
                      copyContent: {
                        ...formState.copyContent,
                        copyWebstore: false
                      }
                    });
                  }}
                  selected={chooseNewThemeSelected}
                />
              </Grid.Col>
            </Grid>
          </AppCard>
        </AppStack>
      )}

      {formState.ecomStockContent.length > 1 &&
        formState.copyThemeFromStockContent && (
          <AppStack style={{ gap: 5 }}>
            <AppStack style={{ gap: 0 }}>
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                Stock content theme
              </AppText>
              <AppText style={{ fontSize: 14, color: '#666' }}>
                Select one of the stock content themes to use for your webstore.
              </AppText>
            </AppStack>
            <AppCard
              radius="md"
              shadow="none"
              style={{ padding: 8 }}
              withBorder
            >
              <Grid>
                {stockContentThemes.map((s) => (
                  <Grid.Col key={s.pkEcomStockContent} span={6}>
                    <CreationWizardButton
                      description={
                        <Anchor
                          component={Link}
                          style={{ color: 'dodgerblue', fontSize: 14 }}
                          target="_blank"
                          to={`/store/${s.ecomStore.urlHandle}`}
                        >
                          View theme
                        </Anchor>
                      }
                      label={s.title}
                      onClick={() => {
                        setFormState({
                          ...formState,
                          ecomStockContent: [
                            ...formState.ecomStockContent.map((c) => ({
                              ...c,
                              useStoreTheme:
                                c.pkEcomStockContent === s.pkEcomStockContent
                            }))
                          ]
                        });
                      }}
                      selected={
                        formState.ecomStockContent.find(
                          (c) =>
                            c.pkEcomStockContent === s.pkEcomStockContent &&
                            c.useStoreTheme
                        ) !== undefined
                      }
                    />
                  </Grid.Col>
                ))}
              </Grid>
            </AppCard>
            <AppText
              style={{
                fontSize: 14,
                color: '#666',
                fontStyle: 'italic',
                textAlign: 'center'
              }}
            >
              You can always switch themes later in the store dashboard.
            </AppText>
          </AppStack>
        )}

      {(!showStoreOrStockThemeOptions || chooseNewThemeSelected) && (
        <AppStack style={{ gap: 5 }}>
          <AppStack style={{ gap: 0 }}>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              Theme layout
            </AppText>
            <AppText style={{ fontSize: 14, color: '#666' }}>
              Choose a layout for your webstore.
            </AppText>
          </AppStack>
          <AppCard radius="md" shadow="none" style={{ padding: 8 }} withBorder>
            <Grid>
              <Grid.Col span={6}>
                <CreationWizardButton
                  description="Best for stores with a small number of products"
                  label="Standard"
                  onClick={() => {
                    setFormState({
                      ...formState,
                      storeSettings: {
                        ...formState.storeSettings,
                        webstoreLayout: 'standard'
                      }
                    });
                  }}
                  selected={
                    formState.storeSettings.webstoreLayout === 'standard'
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <CreationWizardButton
                  description="Great for stores with many products"
                  label="Categorized"
                  onClick={() => {
                    setFormState({
                      ...formState,
                      storeSettings: {
                        ...formState.storeSettings,
                        webstoreLayout: 'categorized'
                      }
                    });
                  }}
                  selected={
                    formState.storeSettings.webstoreLayout === 'categorized'
                  }
                />
              </Grid.Col>
            </Grid>
          </AppCard>
          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              fontStyle: 'italic',
              textAlign: 'center'
            }}
          >
            You can always switch themes later in the store dashboard.
          </AppText>
        </AppStack>
      )}
    </AppStack>
  );
};

StoreCreationWebstoreStep.propTypes = {
  createOptions: PropTypes.object,
  formState: PropTypes.object,
  loading: PropTypes.bool,
  setFormState: PropTypes.func
};

export default StoreCreationWebstoreStep;
